// Reuse the same API client as shopping lists for consistency
import api from './api';

export interface SubscriptionPlan {
    id: string;
    name: string;
    description: string;
    price: number;
    currency: string;
    interval: 'month' | 'year';
    features: string[];
    stripePriceId: string;
    dailyImageLimit: number;
    tier: 'starter' | 'pro' | 'platinum';
    isActive: boolean;
    sortOrder: number;
    createdAt: number;
    updatedAt: number;
}

export interface UsageHistory {
    id: string;
    userId: string;
    date: string;
    imagesGenerated: number;
    tier: 'trial' | 'starter' | 'pro' | 'platinum';
    monthlyLimit: number; // Monthly image limit
    createdAt: number;
    updatedAt: number;
    metadata?: Record<string, string | undefined>;
}

export interface SubscriptionChange {
    id: string;
    userId: string;
    changeType: 'upgrade' | 'downgrade' | 'cancel' | 'reactivate';
    fromTier: 'trial' | 'starter' | 'pro' | 'platinum';
    toTier: 'trial' | 'starter' | 'pro' | 'platinum';
    status: 'pending' | 'completed' | 'cancelled' | 'failed';
    scheduledDate?: number;
    effectiveDate?: number;
    requestedBy: 'user' | 'admin' | 'system' | 'webhook';
    requestSource: 'frontend' | 'admin_panel' | 'webhook' | 'support' | 'api';
    reason?: string;
    createdAt: number;
    updatedAt: number;
    completedAt?: number;
    cancelledAt?: number;
    metadata?: Record<string, any>;
}

export interface SubscriptionInfo {
    stripeCustomerId?: string;
    subscriptionTier?: 'trial' | 'starter' | 'pro' | 'platinum';
    trialEndsAt?: number;
    // Cancellation and downgrade info
    cancelAtPeriodEnd?: boolean;
    currentPeriodEnd?: number;
    scheduledDowngrade?: boolean;
    targetTier?: string;
    // Pending changes
    pendingChanges?: SubscriptionChange[];
    hasPendingChanges?: boolean;
}

export const billingApiService = {
    // Get available subscription plans
    getSubscriptionPlans: async (): Promise<{ success: boolean; plans: SubscriptionPlan[] }> => {
        const response = await api.get('/api/billing/plans');
        return response.data;
    },

    // Create a checkout session for subscription
    createCheckoutSession: async (priceId: string): Promise<{
        success: boolean;
        session?: { sessionId: string; url: string };
        message?: string;
    }> => {
        const response = await api.post('/api/billing/checkout', { priceId });
        return response.data;
    },

    // Create a customer portal session
    createPortalSession: async (): Promise<{ success: boolean; url?: string }> => {
        const response = await api.post('/api/billing/portal', {
            returnUrl: window.location.origin + '/billing',
        });
        return response.data;
    },

    // Get subscription info
    getSubscriptionInfo: async (): Promise<{ success: boolean; subscription?: SubscriptionInfo }> => {
        const response = await api.get('/api/billing/subscription');
        return response.data;
    },

    // Sync subscription data from Stripe
    syncSubscription: async (): Promise<{
        success: boolean;
        updated?: boolean;
        subscription?: any;
        message?: string;
    }> => {
        const response = await api.post('/api/billing/subscription/sync');
        return response.data;
    },

    // Get usage history (replaces payment history)
    getUsageHistory: async (days: number = 30): Promise<{ success: boolean; usage?: UsageHistory[] }> => {
        const response = await api.get(`/api/billing/usage?days=${days}`);
        return response.data;
    },

    // Get subscription changes history (replaces payment history)
    getSubscriptionChanges: async (limit?: number, status?: 'pending' | 'completed' | 'cancelled' | 'failed'): Promise<{
        success: boolean;
        subscriptionChanges: SubscriptionChange[];
    }> => {
        const params = new URLSearchParams();
        if (limit) params.append('limit', limit.toString());
        if (status) params.append('status', status);

        const response = await api.get(`/api/billing/subscription-changes?${params.toString()}`);
        return response.data;
    },

    // Get pending subscription changes
    getPendingSubscriptionChanges: async (): Promise<{
        success: boolean;
        subscriptionChanges: SubscriptionChange[];
    }> => {
        const response = await api.get('/api/billing/subscription-changes/pending');
        return response.data;
    },

    // Cancel a pending subscription change
    cancelSubscriptionChange: async (subscriptionChangeId: string): Promise<{
        success: boolean;
        message?: string;
        subscriptionChange?: SubscriptionChange;
    }> => {
        const response = await api.post(`/api/billing/subscription-changes/${subscriptionChangeId}/cancel`);
        return response.data;
    },

    // Cancel subscription (at period end by default, or immediately)
    cancelSubscription: async (cancelImmediately: boolean = false): Promise<{
        success: boolean;
        message?: string;
        subscription?: {
            id: string;
            cancelAtPeriodEnd: boolean;
            currentPeriodEnd: number;
            status: string;
        };
    }> => {
        const response = await api.post('/api/billing/subscription/cancel', { cancelImmediately });
        return response.data;
    },

    // Reactivate a cancelled subscription
    reactivateSubscription: async (): Promise<{
        success: boolean;
        message?: string;
        subscription?: {
            id: string;
            cancelAtPeriodEnd: boolean;
            status: string;
        };
    }> => {
        const response = await api.post('/api/billing/subscription/reactivate');
        return response.data;
    },

    // Downgrade subscription to lower tier
    downgradeSubscription: async (targetTier: 'starter' | 'pro'): Promise<{
        success: boolean;
        message?: string;
        subscription?: {
            id: string;
            targetTier?: string;
            currentPeriodEnd?: number;
            cancelAtPeriodEnd?: boolean;
        };
        subscriptionChange?: SubscriptionChange;
    }> => {
        const response = await api.post('/api/billing/subscription/downgrade', { targetTier });
        return response.data;
    },

    // Get billing history (invoices from Stripe)
    getBillingHistory: async (limit: number = 10): Promise<{
        success: boolean;
        invoices?: any[];
    }> => {
        const response = await api.get(`/api/billing/invoices?limit=${limit}`);
        return response.data;
    },

    // Download invoice PDF
    downloadInvoice: async (invoiceId: string): Promise<{
        success: boolean;
        url?: string;
        message?: string;
    }> => {
        const response = await api.get(`/api/billing/invoices/${invoiceId}/download`);
        return response.data;
    },

    // Get payment status (for checkout session tracking)
    getPaymentStatus: async (sessionId: string): Promise<{
        success: boolean;
        payment?: {
            id: string;
            status: 'pending' | 'processing' | 'completed' | 'failed' | 'canceled';
            sessionId: string;
            paymentStatus: string;
            amount?: number;
            currency?: string;
        };
        subscriptionUpdated?: boolean;
        message?: string;
    }> => {
        const response = await api.get(`/api/billing/payment-status/${sessionId}`);
        return response.data;
    },

    // Get all user subscriptions with multiple subscription detection
    getAllSubscriptions: async (): Promise<{
        success: boolean;
        subscriptions: Array<{
            id: string;
            status: string;
            tier: string;
            planName: string;
            priceId: string;
            currentPeriodEnd: number;
            cancelAtPeriodEnd: boolean;
        }>;
        hasMultiple: boolean;
        message?: string;
    }> => {
        const response = await api.get('/api/billing/subscriptions/all');
        return response.data;
    },

    // Cancel redundant subscriptions, keeping only the highest tier
    cancelRedundantSubscriptions: async (): Promise<{
        success: boolean;
        canceledSubscriptions: string[];
        keptSubscription?: string;
        message: string;
    }> => {
        const response = await api.post('/api/billing/subscriptions/cancel-redundant');
        return response.data;
    },
};

// Export for backward compatibility
export const billingApi = billingApiService;
